import { createTheme } from "@mui/material/styles";
import type { Components, Theme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import { formControlStyles } from "./formStyles";
import { createElement } from "react";
import { ChevronDown, X, type LucideProps } from "lucide-react";

/*
 * Global MUI theme that mirrors the app design tokens (see src/styles/main.css).
 *
 * Palette colours intentionally reference the CSS custom properties so light /
 * dark mode is driven entirely by the `.dark` class the theme store toggles on
 * <html>. Every colour supplies `light`/`dark`/`contrastText` explicitly so
 * MUI never has to run its colour math against them.
 */

type Mode = "light" | "dark";

const font = "var(--font-sans)";

function SelectIcon(props: LucideProps & { ownerState?: unknown }) {
  const iconProps = { ...props };
  delete iconProps.ownerState;
  return createElement(ChevronDown, iconProps);
}

function palette(mode: Mode) {
  // MUI's colour-manipulation helpers (alpha, darken, lighten) cannot parse
  // CSS var() strings, so every palette colour must be a real hex value.
  // Values are taken from src/styles/main.css; dark-mode overrides match
  // the .dark selector there.
  const d = mode === "dark";

  return {
    mode,
    primary: {
      main: d ? "#43c77d" : "#16824d",
      light: d ? "#43c77d" : "#16824d",
      dark: d ? "#43c77d" : "#16824d",
      contrastText: d ? "#08130d" : "#ffffff",
    },
    secondary: {
      main: d ? "#242a2a" : "#eef1f0",
      light: d ? "#242a2a" : "#eef1f0",
      dark: d ? "#242a2a" : "#eef1f0",
      contrastText: d ? "#edf2f0" : "#18201e",
    },
    error: {
      main: d ? "#ff6467" : "#e7000b",
      light: d ? "#ff6467" : "#e7000b",
      dark: d ? "#ff6467" : "#e7000b",
      contrastText: d ? "#240507" : "#ffffff",
    },
    warning: {
      main: d ? "#fcab00" : "#da8b00",
      light: d ? "#fcab00" : "#da8b00",
      dark: d ? "#fcab00" : "#da8b00",
      contrastText: "#101211",
    },
    info: {
      main: d ? "#6caee3" : "#2276ad",
      light: d ? "#6caee3" : "#2276ad",
      dark: d ? "#6caee3" : "#2276ad",
      contrastText: d ? "#08130d" : "#ffffff",
    },
    success: {
      main: d ? "#5bc476" : "#248a50",
      light: d ? "#5bc476" : "#248a50",
      dark: d ? "#5bc476" : "#248a50",
      contrastText: d ? "#08130d" : "#ffffff",
    },
    background: {
      default: d ? "#111415" : "#f6f7f7",
      paper: d ? "#191d1e" : "#ffffff",
    },
    text: {
      primary: d ? "#edf2f0" : "#18201e",
      secondary: d ? "#a8b1ae" : "#65706c",
      disabled: d ? "#a8b1ae" : "#65706c",
    },
    divider: d ? "#353d3b" : "#dde3e0",
    action: {
      active: d ? "#a8b1ae" : "#65706c",
      hover: d ? "rgba(168,177,174,0.1)" : "rgba(101,112,108,0.1)",
      hoverOpacity: 0.08,
      selected: d ? "rgba(67,199,125,0.08)" : "rgba(22,130,77,0.08)",
      selectedOpacity: 0.08,
      disabled: d ? "rgba(168,177,174,0.4)" : "rgba(101,112,108,0.4)",
      disabledBackground: d ? "rgba(33,38,39,0.4)" : "rgba(240,242,242,0.4)",
      disabledOpacity: 0.4,
      focus: d ? "rgba(67,199,125,0.12)" : "rgba(22,130,77,0.12)",
      focusOpacity: 0.12,
      activatedOpacity: 0.12,
    },
  };
}

const components: Components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        fontFamily: font,
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
      },
      "::selection": {
        backgroundColor: "var(--primary)",
        color: "var(--primary-foreground)",
      },
    },
  },

  MuiButtonBase: {
    styleOverrides: {
      root: {
        fontFamily: font,
      },
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        fontFamily: font,
        textTransform: "none",
        borderRadius: "var(--form-control-radius)",
        fontWeight: 500,
        fontSize: "0.875rem",
        lineHeight: 1.5,
        minHeight: 40,
        letterSpacing: "-0.01em",
        padding: "0.375rem 1rem",
        transition:
          "color 200ms ease, background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
        "&.Mui-focusVisible": {
          outline: "2px solid var(--ring)",
          outlineOffset: 2,
        },
        "&:active:not(.Mui-disabled)": {
          filter: "brightness(0.94)",
          boxShadow: "none",
        },
        "&.Mui-disabled": {
          color: "var(--muted-foreground) !important",
          opacity: 0.5,
          boxShadow: "none",
        },
        "&.Mui-disabled svg": {
          color: "inherit !important",
        },
        "&.MuiButton-loading.MuiButton-loadingPositionCenter": {
          color: "transparent !important",
        },
      },
      sizeSmall: {
        minHeight: 32,
        padding: "0.25rem 0.75rem",
        fontSize: "0.8125rem",
      },
      sizeLarge: {
        minHeight: 44,
        padding: "0.5rem 1.25rem",
        fontSize: "0.875rem",
      },
      loadingIndicator: { color: "var(--muted-foreground)" },
      contained: {
        backgroundColor: "var(--variant-containedBg)",
        color: "var(--variant-containedColor, var(--foreground))",
        boxShadow: "0 1px 2px #00000014",
        "&:hover": {
          backgroundColor:
            "color-mix(in srgb, var(--variant-containedBg) 90%, #000)",
          boxShadow: "0 2px 4px #0000001f",
        },
        "&.Mui-disabled": {
          backgroundColor: "var(--muted)",
          color: "var(--muted-foreground)",
        },
      },
      outlined: ({ ownerState }) => ({
        color: ["primary", "secondary", "inherit"].includes(
          ownerState.color ?? "primary",
        )
          ? "var(--foreground)"
          : "var(--variant-outlinedColor)",
        borderColor: ["primary", "secondary", "inherit"].includes(
          ownerState.color ?? "primary",
        )
          ? "var(--border)"
          : "var(--variant-outlinedBorder)",
        "&:hover": {
          backgroundColor: ["primary", "secondary", "inherit"].includes(
            ownerState.color ?? "primary",
          )
            ? "var(--muted-60)"
            : "var(--variant-outlinedBg)",
        },
      }),
      text: ({ ownerState }) => ({
        color: ["primary", "secondary", "inherit"].includes(
          ownerState.color ?? "primary",
        )
          ? "var(--foreground)"
          : "var(--variant-textColor)",
        "&:hover": {
          backgroundColor: ["primary", "secondary", "inherit"].includes(
            ownerState.color ?? "primary",
          )
            ? "var(--muted-60)"
            : "var(--variant-textBg)",
        },
      }),
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: "var(--form-control-radius)",
        color: "inherit",
        minWidth: "40px !important",
        minHeight: "40px !important",
        "&.Mui-focusVisible": {
          outline: "2px solid var(--ring)",
          outlineOffset: 2,
        },
        "&.Mui-disabled": {
          color: "var(--muted-foreground) !important",
          opacity: 0.5,
        },
        "&.Mui-disabled svg": { color: "inherit !important" },
        "&.MuiIconButton-loading": { color: "transparent !important" },
        "&:active:not(.Mui-disabled)": { filter: "brightness(0.94)" },
      },
      sizeSmall: { minWidth: "32px !important", minHeight: "32px !important" },
      sizeLarge: { minWidth: "44px !important", minHeight: "44px !important" },
      loadingIndicator: { color: "var(--muted-foreground)" },
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: "1rem",
        backgroundColor: "var(--popover)",
        backgroundImage: "none",
        color: "var(--popover-foreground)",
        border: "1px solid var(--border)",
        boxShadow: "0 12px 40px -8px #0000001f, 0 4px 12px #0000000f",
        maxHeight: "min(360px, 60vh)",
        overflowY: "auto",
      },
      list: {
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: "1rem",
        backgroundColor: "var(--popover)",
        backgroundImage: "none",
        color: "var(--popover-foreground)",
        border: "1px solid var(--border)",
        boxShadow: "0 12px 40px -8px #0000001f, 0 4px 12px #0000000f",
      },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontFamily: font,
        minHeight: 36,
        fontSize: "0.875rem",
        fontWeight: 500,
        letterSpacing: "-0.01em",
        color: "var(--popover-foreground)",
        borderRadius: "0.5rem",
        marginTop: 2,
        marginBottom: 2,
        marginLeft: 4,
        marginRight: 4,
        paddingTop: 8,
        paddingBottom: 8,
        paddingLeft: 12,
        paddingRight: 12,
        "&:hover": {
          backgroundColor: "var(--secondary)",
        },
        "&.Mui-selected": {
          backgroundColor: "var(--primary-10)",
          color: "var(--primary)",
          "&:hover": {
            backgroundColor: "var(--primary-14)",
          },
        },
      },
    },
  },

  MuiList: {
    styleOverrides: {
      root: {
        paddingTop: 4,
        paddingBottom: 4,
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        ...formControlStyles,
        fontFamily: font,
        color: "var(--foreground)",
        borderRadius: "var(--form-control-radius)",
        fontSize: "0.875rem",
        backgroundColor: "var(--form-control-bg)",
        transition:
          "color 200ms ease, background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease",
        "&:hover": {
          backgroundColor: "var(--form-control-hover-bg)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--form-control-hover-border)",
          },
        },
        "&.Mui-focused": {
          backgroundColor: "var(--form-control-bg)",
          boxShadow: "var(--form-control-focus-ring)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--primary)",
            borderWidth: "1px",
          },
        },
        "&.Mui-disabled": {
          backgroundColor: "var(--muted-15)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--border)",
          },
        },
        "&.Mui-error": {
          backgroundColor: "var(--form-control-bg)",
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--destructive)",
          },
        },
        "&.Mui-error.Mui-focused": {
          boxShadow: "0 0 0 3px var(--destructive-20)",
        },
        "&.MuiInputBase-multiline": {
          height: "auto",
          padding: "0.75rem 0.875rem",
        },
      },
      notchedOutline: {
        border: "1px solid var(--form-control-border)",
        "& legend": { maxWidth: 0 },
      },
      input: {
        padding: "0.625rem 0.875rem",
        color: "var(--foreground)",
        "&.MuiInputBase-inputMultiline": { padding: 0 },
        "&::placeholder": {
          color: "var(--form-control-placeholder)",
          opacity: 1,
        },
      },
    },
  },

  MuiTextField: {
    defaultProps: {
      size: "small",
    },
  },

  MuiInputLabel: {
    defaultProps: { shrink: true },
    styleOverrides: {
      root: {
        position: "static",
        transform: "none",
        maxWidth: "none",
        marginBottom: "0.375rem",
        color: "var(--foreground)",
        fontSize: "0.8125rem",
        fontWeight: 500,
        lineHeight: 1.4,
        "&.Mui-focused": { color: "var(--foreground)" },
      },
    },
  },

  MuiFormLabel: {
    styleOverrides: {
      asterisk: { color: "var(--destructive)" },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: "0.375rem 0 0",
        fontSize: "0.75rem",
        lineHeight: 1.5,
        overflowWrap: "anywhere",
      },
    },
  },

  MuiPickersOutlinedInput: {
    styleOverrides: {
      root: {
        ...formControlStyles,
        "&:hover .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: "var(--form-control-hover-border)",
        },
        "&.Mui-focused": {
          boxShadow: "var(--form-control-focus-ring)",
          "& .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "var(--primary)",
            borderWidth: 1,
          },
        },
        "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": {
          borderColor: "var(--destructive)",
        },
        "&.Mui-error.Mui-focused": {
          boxShadow: "0 0 0 3px var(--destructive-20)",
        },
      },
      notchedOutline: {
        borderColor: "var(--form-control-border)",
        "& legend": { maxWidth: 0 },
      },
    },
  },

  MuiInputBase: {
    styleOverrides: {
      root: {
        fontFamily: font,
        color: "var(--foreground)",
      },
    },
  },

  MuiSelect: {
    defaultProps: { IconComponent: SelectIcon },
    styleOverrides: {
      select: {
        display: "flex",
        alignItems: "center",
      },
      icon: {
        color: "var(--muted-foreground)",
        width: 18,
        height: 18,
        right: 12,
        top: "calc(50% - 9px)",
      },
      outlined: {
        color: "var(--foreground)",
      },
    },
  },

  MuiAutocomplete: {
    defaultProps: {
      popupIcon: createElement(ChevronDown, { size: 16 }),
      clearIcon: createElement(X, { size: 14 }),
    },
    styleOverrides: {
      inputRoot: {
        height: "auto",
        minHeight: "var(--form-control-height)",
      },
      paper: {
        borderRadius: "1rem",
        backgroundColor: "var(--popover)",
        backgroundImage: "none",
        color: "var(--popover-foreground)",
        border: "1px solid var(--border)",
        boxShadow: "0 12px 40px -8px #0000001f, 0 4px 12px #0000000f",
        marginTop: 4,
      },
      listbox: {
        padding: 4,
      },
      option: {
        fontFamily: font,
        borderRadius: "0.5rem",
        margin: "2px 4px",
        minHeight: 36,
        fontSize: "0.875rem",
        fontWeight: 500,
        '&[aria-selected="true"]': {
          backgroundColor: "var(--primary-10)",
        },
        "&.Mui-focused": {
          backgroundColor: "var(--secondary)",
        },
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: font,
        backgroundColor: "var(--foreground)",
        color: "var(--background)",
        borderRadius: "0.625rem",
        fontSize: "0.75rem",
        fontWeight: 500,
        letterSpacing: "-0.01em",
        lineHeight: 1.4,
        padding: "6px 12px",
        boxShadow: "0 8px 24px #00000024",
        maxWidth: 280,
      },
      arrow: {
        color: "var(--foreground)",
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: "var(--muted)",
        borderRadius: "0.5rem",
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: font,
        borderRadius: 9999,
        fontSize: "0.75rem",
        fontWeight: 500,
        height: 28,
        backgroundColor: "var(--muted-70)",
        color: "var(--foreground)",
        "&.MuiChip-deletable": {
          minHeight: 32,
          height: "auto",
        },
      },
    },
  },

  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: "var(--border)",
      },
    },
  },

  MuiAvatar: {
    styleOverrides: {
      root: {
        fontFamily: font,
        backgroundColor: "var(--primary-10)",
        color: "var(--primary)",
        fontWeight: 600,
        fontSize: "0.8125rem",
      },
    },
  },

  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: "1rem",
        backgroundColor: "var(--card)",
        backgroundImage: "none",
        color: "var(--card-foreground)",
        border: "1px solid var(--border)",
        boxShadow: "0 24px 64px -12px #00000033",
        padding: "1.25rem",
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: 0,
        fontSize: "1.125rem",
        fontWeight: 600,
        letterSpacing: "-0.015em",
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: "1rem 0",
        fontSize: "0.875rem",
        color: "var(--muted-foreground)",
      },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: 0,
        gap: "0.5rem",
      },
    },
  },

  MuiDrawer: {
    styleOverrides: {
      paper: {
        backgroundColor: "var(--card)",
        backgroundImage: "none",
        color: "var(--card-foreground)",
        borderLeft: "1px solid var(--border)",
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        backgroundColor: "var(--primary)",
        height: 2,
        borderRadius: 2,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: "none",
        fontFamily: font,
        fontWeight: 500,
        fontSize: "0.875rem",
        letterSpacing: "-0.01em",
        minHeight: 40,
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        backgroundImage: "none",
        "&.MuiCard-outlined": {
          borderRadius: "1rem",
          boxShadow: "none",
        },
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: {
        "&:last-child": {
          paddingBottom: "1.25rem",
        },
      },
    },
  },

  MuiBackdrop: {
    styleOverrides: {
      root: {
        "&:not(.MuiBackdrop-invisible)": {
          backgroundColor: "var(--backdrop)",
          backdropFilter: "blur(4px)",
        },
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        fontFamily: font,
        borderRadius: "0.625rem",
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: "var(--table-header-bg)",
      },
      stickyHeader: {
        backgroundColor: "var(--table-header-bg)",
      },
      root: {
        fontFamily: font,
        color: "inherit",
        borderBottom: "1px solid var(--border)",
        verticalAlign: "middle",
        fontSize: "0.8125rem",
        lineHeight: 1.5,
      },
    },
  },

  MuiCheckbox: {
    styleOverrides: {
      root: {
        padding: 0,
        color: "var(--muted-foreground)",
        borderRadius: 0,
        transition: "none",
        "&:hover": {
          backgroundColor: "transparent",
        },
        "&.Mui-checked, &.MuiCheckbox-indeterminate": {
          color: "var(--primary)",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 18,
        },
      },
    },
  },

  MuiRadio: {
    styleOverrides: {
      root: {
        padding: 0,
        color: "var(--muted-foreground)",
        transition: "none",
        "&:hover": {
          backgroundColor: "transparent",
        },
        "&.Mui-checked": {
          color: "var(--primary)",
        },
        "& .MuiSvgIcon-root": {
          fontSize: 18,
        },
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: {
        width: 42,
        height: 24,
        padding: 0,
        margin: 0,
        "& .MuiSwitch-switchBase": {
          padding: 3,
          "&.Mui-checked": {
            transform: "translateX(18px)",
            color: "var(--background)",
            "& + .MuiSwitch-track": {
              backgroundColor: "var(--primary)",
              opacity: 1,
            },
          },
        },
        "& .MuiSwitch-thumb": {
          width: 18,
          height: 18,
          boxShadow: "none",
        },
        "& .MuiSwitch-track": {
          borderRadius: 999,
        },
        "&.MuiSwitch-sizeSmall": {
          width: 28,
          height: 16,
          "& .MuiSwitch-switchBase": {
            padding: 2,
            "&.Mui-checked": {
              transform: "translateX(12px)",
            },
          },
          "& .MuiSwitch-thumb": {
            width: 12,
            height: 12,
          },
          "& .MuiSwitch-track": {
            borderRadius: 999,
          },
        },
      },
    },
  },
};

export function createAppTheme(mode: Mode): Theme {
  const theme = createTheme({
    palette: palette(mode),
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: font,
      htmlFontSize: 16,
      button: {
        fontFamily: font,
        textTransform: "none",
        fontWeight: 500,
        letterSpacing: "-0.01em",
      },
      h1: { fontFamily: font, letterSpacing: "-0.03em", fontWeight: 700 },
      h2: { fontFamily: font, letterSpacing: "-0.02em", fontWeight: 650 },
      h3: { fontFamily: font, letterSpacing: "-0.02em", fontWeight: 600 },
      h4: { fontFamily: font, letterSpacing: "-0.015em", fontWeight: 600 },
      h5: { fontFamily: font, letterSpacing: "-0.015em", fontWeight: 600 },
      h6: { fontFamily: font, letterSpacing: "-0.01em", fontWeight: 600 },
      subtitle1: { fontFamily: font, letterSpacing: "-0.01em" },
      subtitle2: { fontFamily: font, letterSpacing: "-0.01em" },
      body1: { fontFamily: font },
      body2: { fontFamily: font },
      caption: { fontFamily: font },
      overline: { fontFamily: font },
    },
    components,
  });

  return theme;
}
